/**
 * @GestionalePD User national federation page UI
 */
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Paper,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  Box,
  InputAdornment,
  TablePagination,
  TableFooter,
  TableCell,
  TableRow,
  Container,
  Card,
  useMediaQuery,
  Stack,
  Checkbox,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BeenhereIcon from '@mui/icons-material/Beenhere';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
//------------------------------------------------------------------------------------------------
import { CustomTextField } from '../../utils/StyledInputs';
import { HttpErrorHandler } from '../../utils/ErrorHandler';
import { LoadingUI } from '../../utils/LayoutStyles';
import { TablePaginationActions } from '../../utils/TableStyles';
import { entityTypeLabel } from '../../utils/EntityTypeLabel';
import { DefaultContainedButton, CustomIconButton } from '../../utils/Buttons';
import { DecisionDialog } from '../../utils/DecisionDialog';
import API from '../../api';

/**
 * @returns User national federation page UI
 */
export default function NationalFederation() {
  // general
  const navigate = useNavigate();
  const { state } = useLocation();
  const { federationID } = state;
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setLoading] = useState(true);

  // entity data
  const [entities, setEntities] = useState([]);
  const [entityTypeData, setEntityTypeData] = useState({});

  // Table pagination handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - entities.length) : 0;
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // refresh federation
  const refreshFederation = () => {
    setErrorMessage('');

    API.get(`/etypes/${federationID}?expand=entities`)
      .then((response) => {
        setEntities(response.data.entity_type.entities);
        setEntityTypeData(response.data.entity_type);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      })
      .finally(() => setLoading(false));
  };

  // search handler
  const [searchMaxLimit, setSearchMaxLimit] = useState(100);
  const [onSearch, setSearch] = useState(false);

  // search request
  const onSearchRequest = (searchedVal) => {
    const name = searchedVal.target.value;
    setSearch(true);
    setErrorMessage('');

    if (name.length <= searchMaxLimit) {
      API.get(`/etypes/${federationID}?expand=entities&search=${name}`)
        .then((response) => {
          setEntities(response.data.entity_type.entities);

          if (response.data.entity_type.entities.length === 0) {
            setSearchMaxLimit(name.length);
          } else {
            setSearchMaxLimit(name.length + 1);
          }

          if (name.length === 0) {
            setSearch(false);
          }
        })
        .catch((error) => {
          setSearch(false);
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  const [openFollowEntityDialog, setOpenFollowEntityDialog] = useState(false);
  const [followEntity, setFollowEntity] = useState(false);
  const [entityToFollow, setEntityToFollow] = useState(null);

  const handleOpenFollowEntityDialog = (entity) => {
    setFollowEntity(!!entity.is_subscribed);
    setEntityToFollow(entity);
    setOpenFollowEntityDialog(true);
  };

  const handleCloseFollowEntityDialog = () => {
    setOpenFollowEntityDialog(false);
    setFollowEntity(null);
    setEntityToFollow(null);
  };

  const submitFollowEntity = (e) => {
    e.preventDefault();

    const userInfo = JSON.parse(localStorage.getItem('userData'));

    const data = {
      user_id: userInfo.id,
      entity_id: entityToFollow.id,
    };

    if (followEntity) {
      API.put(`/userfollow`, data).then((response) => {
        handleCloseFollowEntityDialog();
        refreshFederation();
      });
    } else {
      API.post('/userfollow', data).then((response) => {
        handleCloseFollowEntityDialog();
        refreshFederation();
      });
    }
  };

  // initial federation data loading
  useEffect(() => {
    refreshFederation();
  }, []);

  // if entity id not found redirect to 404 page
  if (!federationID) return <Navigate to={{ pathname: '/404' }} />;

  return !isLoading ? (
    <Container maxWidth="xl">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        role="presentation"
        sx={{ marginBottom: '24px' }}
      >
        <Typography
          sx={{
            height: '24px',
            width: 'fit-content',
            whiteSpace: 'nowrap',
            borderRadius: '6px',
            padding: '0 10px',
            textAlign: 'center',
            fontWeight: '700',
            fontSize: '1.1em',
          }}
        >
          {entityTypeData.name}
        </Typography>
      </Box>

      <Card sx={{ padding: '24px 24px 6px' }}>
        {/* search filed */}
        <Box sx={{ marginBottom: '20px' }}>
          <CustomTextField
            fullWidth
            id="national-org-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder="Cerca..."
            onChange={onSearchRequest}
          />
        </Box>

        {/* organization table */}
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 700 }} aria-label="Federation table">
            {/* organization table header */}
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '70px' }} />
                <TableCell sx={{ width: '200px' }}>Tipo Entità</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell sx={{ width: '300px' }} />
              </TableRow>
            </TableHead>

            {/* organization table body */}
            {entities.length !== 0 ? (
              <TableBody>
                {(rowsPerPage > 0
                  ? entities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : entities
                ).map((entity) => (
                  <TableRow hover key={entity.id}>
                    <TableCell sx={{ maxWidth: '70px' }}>
                      <Tooltip
                        title={entity.is_subscribed ? 'Non seguire entità' : 'Segui entità'}
                        placement="bottom"
                        arrow
                      >
                        <Checkbox
                          onClick={() => handleOpenFollowEntityDialog(entity)}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#d6f5e0',
                            },
                          }}
                          icon={<ControlPointOutlinedIcon />}
                          checkedIcon={<BeenhereIcon />}
                          checked={!!entity.is_subscribed}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: '200px' }}>{entityTypeLabel(entity, true)}</TableCell>
                    <TableCell>{entity.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" justifyContent="end" gap="12px">
                        <Tooltip title="Anagrafica" arrow>
                          <Box>
                            <CustomIconButton
                              onClick={() =>
                                navigate('/user/organization/federations/federation-details', {
                                  state: { federationID: entity.id },
                                })
                              }
                            >
                              <DescriptionOutlinedIcon />
                            </CustomIconButton>
                          </Box>
                        </Tooltip>
                        <Tooltip title="Sottolivelli" arrow>
                          <Box>
                            <CustomIconButton
                              onClick={() =>
                                navigate('/user/organization/federations/federation-child', {
                                  state: { federationID: entity.id },
                                })
                              }
                            >
                              <AccountTreeOutlinedIcon />
                            </CustomIconButton>
                          </Box>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                <DecisionDialog
                  openDialog={openFollowEntityDialog}
                  dialogTitle={`${followEntity ? "Smetti di seguire l'entità" : "Segui l'entità"} ${
                    entityToFollow ? entityToFollow.name : ''
                  }`}
                  dialogDescription={`Sei sicuro di voler ${
                    followEntity
                      ? 'smettere di seguire e quindi di ricevere aggiornamenti e comunicazioni relative alle attività organizzate da '
                      : "iniziare a seguire l'entità selezionata e quindi di ricevere aggiornamenti e comunicazioni relative alle attività organizzate da"
                  } ${entityToFollow ? entityToFollow.name : ''}?`}
                  closeOperation={handleCloseFollowEntityDialog}
                  requestOperation={submitFollowEntity}
                  requestText={followEntity ? 'Smetti si seguire' : 'Segui'}
                  closeText={'Chiudi'}
                  RequestButton={DefaultContainedButton}
                />

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                    Nessuna entità trovata
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            {/* organization table footer with pagination */}
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 'none' }}
                  rowsPerPageOptions={[]}
                  colSpan={5}
                  count={entities.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'organization per page',
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  ) : (
    LoadingUI()
  );
}
